/**
 * Pure math for fit-to-width tables (see src/utils/fitRegistry.ts).
 *
 * A table's text is scaled by `--fit-scale` ∈ [floor, 1] so that its natural
 * width fits the width available; the floor keeps text from going below
 * MIN_FONT_PX. Below the floor we stop shrinking and let the wrapper scroll.
 */

export const MIN_FONT_PX = 10;

// Absorbs sub-pixel rounding so a "fitted" table never shows a 1px scrollbar.
const SLACK = 0.995;

/** Smallest scale that keeps `basePx` text at or above `minPx`. */
export function minScaleFor(basePx: number, minPx: number = MIN_FONT_PX): number {
  if (!(basePx > 0)) return 1;
  return Math.min(1, minPx / basePx);
}

/**
 * First estimate: proportional shrink, clamped to [floor, 1].
 * Returns 1 when the content already fits or the inputs are unusable.
 */
export function computeScale(
  naturalPx: number,
  availablePx: number,
  basePx: number,
  minPx: number = MIN_FONT_PX,
): number {
  if (!(naturalPx > 0) || !(availablePx > 0)) return 1;
  if (naturalPx <= availablePx) return 1;
  const wanted = (availablePx / naturalPx) * SLACK;
  return Math.max(minScaleFor(basePx, minPx), Math.min(1, wanted));
}

/**
 * Convergence step: shrinking is sub-linear (1px borders, fixed controls,
 * wrapped headers un-wrapping), so after applying a scale we re-measure and
 * scale the current value by the residual ratio. Keeps the value when it fits.
 */
export function refineScale(
  current: number,
  measuredPx: number,
  availablePx: number,
  basePx: number,
  minPx: number = MIN_FONT_PX,
): number {
  if (!(measuredPx > 0) || !(availablePx > 0)) return current;
  if (measuredPx <= availablePx) return current;
  const wanted = current * (availablePx / measuredPx) * SLACK;
  return Math.max(minScaleFor(basePx, minPx), Math.min(1, wanted));
}
